import { useCompanyFeature } from 'utils/useCompanyFeature';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Chip, Divider, IconButton, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChatPanelContext, {
  VoiceRecordingContext,
} from 'components/@home/@messages/ChatPanel/ChatPanelContext';
import ContextMessage from 'components/@home/@messages/ChatPanel/SendForm/ContextMessage';
import useVoiceCall from 'components/@home/@messages/ChatPanel/VoiceCall/useVoiceCall';
import { Field, Form } from 'formik';
import findLast from 'lodash/findLast';
import { bool, func, object, string } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useListMessagesQuery } from 'store/app/entities/MessagesApiSlice';
import { I18n, Translate } from 'utils/i18n';
import isSuperAdminSelector from 'store/selectors/isSuperAdmin';
import useChatComposerLinkPopover from 'src/hooks/useChatComposerLinkPopover';
import applyContentEditableFormat from 'utils/applyContentEditableFormat';
import compressImages from 'utils/compressImages';
import wrapSelectionAtCaret from 'utils/wrapSelectionAtCaret';
import canTagInitiative from 'utils/canTagInitiative';
import {
  getOmmnioSignMarketingSignUrl,
  isActiveOmmnioSignProvider,
  isOmmnioSignProvider,
} from 'utils/eSignatureProviderAccess';

import Actions from './Actions';
import AiAssistantBar from './AiAssistantBar';
import InitiativeTagSelector from './InitiativeTagSelector';
import ComposerActionsMenu from './ComposerActionsMenu';
import FormatToolbar from './buttons/FormatToolbar';
import LinkPopover from './buttons/LinkPopover';
import PickEmojiButton from './buttons/PickEmojiButton';
import SendButton from './buttons/SendButton';
import VoiceRecordingPanel from './VoiceRecording/VoiceRecordingPanel';
import connector from './connector';
import Container from './Container';
import formik from './formik';
import ChatField from './formik/ChatField';
import ChatSelect from './formik/ChatSelect';
import {
  Bloom,
  MicControl,
  useAmplitudeVars,
  isAgentFloor,
  agentHue,
  userHue,
  phaseLabelKey,
} from './VoiceMode';

const renderBoldChunk = chunks => (
  <Box component="strong" sx={{ mr: 0.5 }}>
    {chunks}
  </Box>
);

const SendMessageForm = ({
  channel,
  isDirect,
  isCompanyAdmin,
  isTeamAdmin,
  authCompany = null,
  setFieldValue,
  submitForm,
  values: { message, sentAsChannel, initiativeTag },
  actions,
  authEmployee,
  authUser,
  onChangeSentAsChannel,
  contextMessage = null,
  locale = null,
  scrollChatToBottom = undefined,
}) => {
  const {
    _id,
    isAdmin,
    canWriteAsChannel,
    handoffFrom,
    company_id: companyId,
    isApp,
    agentId,
  } = channel;
  const suggestionsPortal = useRef();
  const inputRef = useRef();
  const savedSelection = useRef(null);
  const {
    linkPopover,
    closeLinkPopover,
    handleLinkToolbarClick,
    handleLinkEditFromInput,
    handleLinkApply,
    handleLinkRemove,
    handleLinkShortcut,
  } = useChatComposerLinkPopover(inputRef);
  const [formatState, setFormatState] = useState({
    bold: false,
    italic: false,
    strikethrough: false,
    code: false,
    link: false,
  });
  const { setImagesToUpload } = useContext(ChatPanelContext);
  const voiceRecording = useContext(VoiceRecordingContext);
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const areInitiativesEnabled = useCompanyFeature('initiatives');
  const areArticlesEnabled = useCompanyFeature('articles') || isSuperAdmin;
  const isAiWritingAssistantEnabled = useCompanyFeature('aiWritingAssistant');
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const theme = useTheme();
  const composerRootRef = useRef(null);
  const voice = useVoiceCall({ agentId, appUserId: _id, channel });
  const voiceActive = voice.state.status === 'connecting' || voice.state.status === 'active';
  useAmplitudeVars(composerRootRef, {
    active: voiceActive,
    getLevels: voice.getLevels,
    phase: voice.phase,
  });
  const voiceTint = isAgentFloor(voice.phase) ? agentHue(theme) : userHue(theme);
  const voicePlaceholder = voiceActive
    ? voice.userLiveText || I18n.t(phaseLabelKey(voice.phase))
    : undefined;

  useEffect(() => {
    if (!voiceActive || !scrollChatToBottom) return;
    scrollChatToBottom();
  }, [voiceActive, voice.userLiveText, voice.phase, scrollChatToBottom]);

  const finishConversation = I18n.t('ChatFlow.Finish conversation');
  const { channelId } = useParams();
  const { currentData: messagesData } = useListMessagesQuery({ channelId }, { skip: !channelId });
  const suggestions = useMemo(() => {
    const msgs = messagesData?.messages;
    const userId = authUser?._id;
    if (!msgs?.length || !userId) return [];
    const sorted = [...msgs].sort((a, b) => {
      if (!a.createdAt) return -1;
      if (!b.createdAt) return 1;
      return new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf();
    });
    const lastNonUserMessage = findLast(sorted, m => {
      if (m.deletedAt || m.isDeleted) return false;
      const senderId = m.user_id || m.from?._id;
      return senderId !== userId;
    });
    const list = [...(lastNonUserMessage?.suggestions || [])];
    if (handoffFrom && !list.includes(finishConversation)) {
      list.push(finishConversation);
    }
    return list;
  }, [authUser?._id, messagesData?.messages, handoffFrom, finishConversation]);
  useEffect(() => {
    onChangeSentAsChannel(sentAsChannel);
  }, [onChangeSentAsChannel, sentAsChannel]);
  useEffect(() => {
    if (contextMessage && inputRef.current) {
      inputRef.current.focus();
    }
  }, [contextMessage]);

  useEffect(() => {
    const currentMarkup =
      typeof message === 'string' ? message : String(message?.markup ?? message?.text ?? '');
    if (!currentMarkup.trim()) {
      setFormatState({
        bold: false,
        italic: false,
        strikethrough: false,
        code: false,
        link: false,
      });
    }
  }, [message]);

  const saveSelection = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (sel.rangeCount && el.contains(sel.anchorNode)) {
      savedSelection.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (savedSelection.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection.current);
      savedSelection.current = null;
    }
  }, []);

  const onPickEmoji = useCallback(picked => {
    const el = inputRef.current;
    if (!el) return;
    if (el.contentEditable !== 'true') return;
    el.focus();
    const sel = window.getSelection();
    let range;
    if (savedSelection.current && el.contains(savedSelection.current.startContainer)) {
      range = savedSelection.current;
      range.collapse(true);
    } else if (sel.rangeCount && el.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
      range.collapse(true);
    } else {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }
    const textNode = document.createTextNode(picked);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    savedSelection.current = range.cloneRange();
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  const handleWrapSelection = useCallback(
    (prefix, suffix) => {
      if (!inputRef.current) return;
      const input = inputRef.current;

      if (input.contentEditable === 'true') {
        applyContentEditableFormat(input, prefix, suffix);
        return;
      }

      const hasSelection = input.selectionEnd > input.selectionStart;
      const wrapPrefix = hasSelection ? prefix : ` ${prefix}`;
      const wrapSuffix = hasSelection ? suffix : `${suffix} `;
      const range = wrapSelectionAtCaret(input, wrapPrefix, wrapSuffix, newValue =>
        setFieldValue('message', {
          ...message,
          markup: newValue,
          text: newValue,
        }),
      );
      if (range && inputRef.current) {
        const start = range.newSelectionStart;
        const end = range.newSelectionEnd;
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.selectionStart = start;
            inputRef.current.selectionEnd = end;
            inputRef.current.focus();
          }
        }, 10);
      }
    },
    [message, setFieldValue],
  );

  const handleAttachmentChange = async event => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const images = await compressImages(files);
    setImagesToUpload(images);
    event.target.value = '';
  };

  const canOpenAdvancedActions =
    isAdmin || canWriteAsChannel || isCompanyAdmin || (isDirect && isTeamAdmin);
  const canAttachInitiative = canTagInitiative(channel, isCompanyAdmin);

  const ommnioSignInactiveMarketingUrl = useMemo(() => {
    if (
      !authCompany ||
      !isOmmnioSignProvider(authCompany) ||
      isActiveOmmnioSignProvider(authCompany)
    ) {
      return undefined;
    }
    return getOmmnioSignMarketingSignUrl(locale);
  }, [authCompany, locale]);

  const canOpenDigitalSignature = canOpenAdvancedActions;
  const composerTrimmed = String(message?.markup ?? message?.text ?? '').trim();
  const suggestionDisabled = composerTrimmed.length > 0;

  const renderSuggestion = useCallback(
    s => (
      <Chip
        disabled={suggestionDisabled}
        sx={{ mr: 0 }}
        key={s}
        label={s}
        size="small"
        variant="outlined"
        clickable
        onClick={async e => {
          e.preventDefault();
          if (s === finishConversation) {
            actions.chatFlows.conversationRestart(handoffFrom);
            actions.channels.setArchived(companyId, authEmployee._id, _id, true);
          } else {
            await setFieldValue('message', { text: s, markup: s, mentions: [] });
            submitForm();
          }
        }}
      />
    ),
    [
      _id,
      actions.channels,
      actions.chatFlows,
      authEmployee._id,
      companyId,
      finishConversation,
      handoffFrom,
      setFieldValue,
      submitForm,
      suggestionDisabled,
    ],
  );

  if (voiceRecording.isRecording) {
    return (
      <Box sx={{ position: 'relative' }}>
        <VoiceRecordingPanel
          recordingTime={voiceRecording.recordingTime}
          waveformHeights={voiceRecording.waveformHeights}
          isPaused={voiceRecording.isPaused}
          previewRecordingUrl={voiceRecording.previewRecordingUrl}
          previewWaveformHeights={voiceRecording.previewWaveformHeights}
          onCancel={voiceRecording.cancelRecording}
          onSend={voiceRecording.stopAndSend}
          onPause={voiceRecording.pauseRecording}
          onResume={voiceRecording.resumeRecording}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }} ref={composerRootRef}>
      <Box
        ref={suggestionsPortal}
        sx={{
          border: 0,
          maxHeight: '25vh',
          overflowY: 'scroll',
          position: 'relative',
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '100%',
          height: 150,
          overflow: 'hidden',
          pointerEvents: 'none',
          opacity: voiceActive ? 1 : 0,
          transform: voiceActive ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity .5s ease, transform .55s cubic-bezier(.2,.7,.2,1)',
          zIndex: 0,
        }}
      >
        <Bloom phase={voice.phase} />
      </Box>
      <Box
        component={Form}
        sx={{
          margin: theme.spacing(0, 1.5),
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          overflow: 'hidden',
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.secondary.ultraUltraLight,
          }),
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: theme.palette.background.paper,
          }),
          ...(voiceActive && {
            transition: 'border-color .45s ease, box-shadow .45s ease',
            borderColor: alpha(voiceTint, 0.55),
            boxShadow: `0 6px 28px ${alpha(voiceTint, 0.13)}`,
          }),
        }}
      >
        {contextMessage ? (
          <ContextMessage message={contextMessage.message} variant={contextMessage.type} />
        ) : null}
        {!voiceActive && suggestions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: theme.spacing(1),
              padding: theme.spacing(1, 1.25),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {suggestions.map(renderSuggestion)}
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {!contextMessage && isAiWritingAssistantEnabled && aiAssistantOpen ? (
            <AiAssistantBar
              onClose={() => setAiAssistantOpen(false)}
              onGeneratedMessage={text => {
                setFieldValue('message', { text, markup: text, mentions: [] });
              }}
            />
          ) : null}
          <Container>
            {(isAdmin || canWriteAsChannel || isCompanyAdmin) && !isDirect && (
              <Field name="sentAsChannel" component={ChatSelect} />
            )}
            <Field
              name="message"
              component={ChatField}
              suggestionsPortal={suggestionsPortal}
              inputRef={inputRef}
              voicePlaceholder={voicePlaceholder}
              onFormatStateChange={setFormatState}
              onLinkShortcut={handleLinkShortcut}
              onLinkEdit={handleLinkEditFromInput}
            />
          </Container>
          <Actions
            sx={{
              minHeight: 40,
              marginTop: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 40,
              }}
            >
              <FormatToolbar
                onWrapSelection={handleWrapSelection}
                onLinkClick={handleLinkToolbarClick}
                activeFormats={formatState}
              />
              {!contextMessage && isAiWritingAssistantEnabled ? (
                <>
                  <Divider orientation="vertical" sx={{ mx: 1, height: '1rem' }} />
                  <Tooltip title={I18n.t('AiWritingAssistant.buttonLabel')}>
                    <IconButton
                      size="small"
                      sx={{
                        padding: theme.spacing(0.5),
                        borderRadius: theme.shape.borderRadius,
                        ...(aiAssistantOpen && {
                          backgroundColor: theme.palette.action.selected,
                          '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                          },
                        }),
                      }}
                      color={aiAssistantOpen ? 'primary' : 'default'}
                      onClick={() => setAiAssistantOpen(prev => !prev)}
                      aria-label={I18n.t('AiWritingAssistant.buttonLabel')}
                      aria-pressed={aiAssistantOpen}
                    >
                      <AutoAwesomeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : null}
              {!contextMessage && areInitiativesEnabled && canAttachInitiative ? (
                <>
                  <Divider orientation="vertical" sx={{ mx: 1, height: '1rem' }} />
                  <InitiativeTagSelector
                    value={initiativeTag}
                    onChange={value => setFieldValue('initiativeTag', value)}
                  />
                </>
              ) : null}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1),
              }}
            >
              {!contextMessage ? (
                <ComposerActionsMenu
                  onAttachmentChange={handleAttachmentChange}
                  canOpenAdvanced={canOpenAdvancedActions}
                  canOpenDigitalSignature={canOpenDigitalSignature}
                  {...(ommnioSignInactiveMarketingUrl ? { ommnioSignInactiveMarketingUrl } : {})}
                  canSendArticle={areArticlesEnabled}
                />
              ) : null}
              <PickEmojiButton
                onPick={onPickEmoji}
                onOpen={saveSelection}
                onClose={restoreSelection}
                locale={locale}
              />
              {voiceActive ? (
                <MicControl
                  muted={voice.muted}
                  onToggleMute={voice.toggleMute}
                  devices={voice.devices}
                  onSwitchDevice={voice.switchDevice}
                />
              ) : null}
              <SendButton
                voiceActive={voiceActive}
                hasText={composerTrimmed.length > 0}
                onStopVoiceCall={voice.end}
                useVoiceCallWhenEmpty={composerTrimmed.length === 0 && isApp && !!agentId}
                onStartVoiceCall={voice.start}
                useMicWhenEmpty={composerTrimmed.length === 0}
                onStartVoiceRecording={voiceRecording.startRecording}
              />
            </Box>
          </Actions>
        </Box>
      </Box>
      <Box
        sx={{
          fontSize: 11,
          color: theme.palette.text.secondary,
          textAlign: 'right',
          paddingRight: theme.spacing(3),
          marginTop: theme.spacing(0.25),
          lineHeight: 1.2,
          opacity: message?.text?.trim() ? 0.65 : 0,
          transition: 'opacity 0.4s ease',
          marginBottom: theme.spacing(0.5),
        }}
      >
        <Translate value="SendForm.shiftEnterForNewLine" tag="span" b={renderBoldChunk} />
      </Box>
      <LinkPopover
        open={linkPopover.open}
        anchorEl={linkPopover.anchorEl}
        onClose={closeLinkPopover}
        onApply={handleLinkApply}
        onRemove={linkPopover.showRemove ? handleLinkRemove : undefined}
        initialUrl={linkPopover.initialUrl}
        initialText={linkPopover.initialText}
        showRemove={linkPopover.showRemove}
      />
    </Box>
  );
};
SendMessageForm.propTypes = {
  channel: object.isRequired,
  isDirect: bool.isRequired,
  isCompanyAdmin: bool.isRequired,
  isTeamAdmin: bool.isRequired,
  authCompany: object,
  locale: string,
  setFieldValue: func.isRequired,
  submitForm: func.isRequired,
  values: object.isRequired,
  actions: object.isRequired,
  authEmployee: object.isRequired,
  authUser: object.isRequired,
  onChangeSentAsChannel: func.isRequired,
  scrollChatToBottom: func,
  contextMessage: object,
};

export default connector(formik(SendMessageForm));
